import type { ClearableSiteField, Site, SiteAddress, SiteContactPatch } from './inventory';

/**
 * Thrown when the server answered OK but didn't keep part of a site change.
 * An older server drops fields it doesn't know (e.g. a rolled-back release
 * without ordered_systems), so an OK alone doesn't mean the
 * change was saved (Heidi, tidaltech/koi#8261).
 */
export class SiteNotSavedError extends Error {
  constructor(readonly fields: ClearableSiteField[]) {
    super(`not saved: ${fields.join(', ')}`);
    this.name = 'SiteNotSavedError';
  }
}

// The server trims values and collapses runs of spaces; that isn't a lost change.
const tidy = (value: string | undefined) => (value ?? '').split(/\s+/).filter(Boolean).join(' ');

function sameAddress(a: SiteAddress | undefined, b: SiteAddress | undefined): boolean {
  const norm = (x: SiteAddress | undefined) =>
    JSON.stringify([tidy(x?.line1), tidy(x?.line2), tidy(x?.postcode), tidy(x?.city)]);
  return norm(a) === norm(b);
}

/** The fields of `patch` that `saved` doesn't reflect. */
export function unsavedSiteFields(patch: SiteContactPatch, saved: Site): ClearableSiteField[] {
  const out: ClearableSiteField[] = [];
  if (patch.address !== undefined && !sameAddress(saved.address, patch.address)) {
    out.push('address');
  }
  if (patch.orderedSystems !== undefined && saved.orderedSystems !== patch.orderedSystems) {
    out.push('orderedSystems');
  }
  for (const field of patch.clear ?? []) {
    const left =
      field === 'address' ? saved.address && !sameAddress(saved.address, undefined) : saved[field];
    if (left !== undefined && left !== false && !out.includes(field)) out.push(field);
  }
  return out;
}

/** Throws SiteNotSavedError unless `saved` reflects every field of `patch`. */
export function assertSiteSaved(patch: SiteContactPatch, saved: Site): Site {
  const missing = unsavedSiteFields(patch, saved);
  if (missing.length > 0) throw new SiteNotSavedError(missing);
  return saved;
}
